import React, { useEffect, useState } from 'react';
import Layout from '../../components/layout/Layout';
import CameraCapture from '../../components/ui/CameraCapture';
import { useLocation } from '../../hooks/useLocation';
import api from '../../api/axios';
import toast from 'react-hot-toast';

export default function TakeAttendance() {
  const [photo, setPhoto] = useState(null);
  const [todayRecord, setTodayRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { location, error: locationError, loading: locationLoading, getLocation } = useLocation();

  useEffect(() => {
    api.get('/attendance/today')
      .then(res => setTodayRecord(res.data.attendance))
      .catch(() => {})
      .finally(() => setLoading(false));
    getLocation();
  }, []);

  const canSignIn = !todayRecord || !todayRecord.sign_in_time;
  const canSignOut = todayRecord?.sign_in_time && !todayRecord?.sign_out_time;
  const done = todayRecord?.sign_in_time && todayRecord?.sign_out_time;

  async function handleSubmit(type) {
    if (!photo) { toast.error('Please take a photo first'); return; }
    if (!location) { toast.error('Location not available. Please allow location access.'); return; }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('photo', photo, 'attendance.jpg');
      formData.append('latitude', location.latitude);
      formData.append('longitude', location.longitude);
      formData.append('address', location.address || '');
      formData.append('device', navigator.userAgent);

      const endpoint = type === 'in' ? '/attendance/sign-in' : '/attendance/sign-out';
      const res = await api.post(endpoint, formData, { headers: { 'Content-Type': 'multipart/form-data' } });

      toast.success(res.data.message);
      // Refresh
      const updated = await api.get('/attendance/today');
      setTodayRecord(updated.data.attendance);
      setPhoto(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <Layout><div style={{ textAlign: 'center', padding: '3rem' }}><span className="spinner spinner-dark" /></div></Layout>;

  return (
    <Layout>
      <div className="page-header">
        <h1>Take Attendance</h1>
        <p>Take a live photo and allow location access to record your attendance.</p>
      </div>

      {done && (
        <div className="alert alert-success">
          ✅ You have completed your attendance for today. Sign-in at {new Date(todayRecord.sign_in_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}, sign-out at {new Date(todayRecord.sign_out_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
        {/* Camera section */}
        <div className="card">
          <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>📷 Live Photo</h2>
          <CameraCapture
            onCapture={setPhoto}
            label={canSignIn ? 'Capture Sign-In Photo' : 'Capture Sign-Out Photo'}
          />
        </div>

        {/* Location & submit */}
        <div className="card">
          <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>📍 Location</h2>

          {locationLoading && (
            <div style={{ textAlign: 'center', padding: '1.5rem', color: '#64748b' }}>
              <span className="spinner spinner-dark" /> Getting location...
            </div>
          )}
          {locationError && <div className="alert alert-error">{locationError}</div>}
          {location && (
            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '0.875rem', marginBottom: '1rem' }}>
              <div style={{ color: '#166534', fontSize: '0.875rem', fontWeight: 500 }}>✅ Location acquired</div>
              <div style={{ color: '#15803d', fontSize: '0.8rem', marginTop: '0.25rem' }}>{location.address || `${location.latitude.toFixed(5)}, ${location.longitude.toFixed(5)}`}</div>
            </div>
          )}

          {!location && !locationLoading && (
            <button className="btn btn-outline btn-block" onClick={getLocation} style={{ marginBottom: '1rem' }}>
              🔄 Retry Location
            </button>
          )}

          {/* Today's status summary */}
          <div style={{ background: '#f8fafc', borderRadius: 10, padding: '1rem', marginBottom: '1.25rem' }}>
            <h3 style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600, marginBottom: '0.75rem' }}>TODAY'S STATUS</h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
              <span>Sign In</span>
              <span style={{ fontWeight: 600, color: todayRecord?.sign_in_time ? '#16a34a' : '#94a3b8' }}>
                {todayRecord?.sign_in_time ? new Date(todayRecord.sign_in_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Not recorded'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', marginTop: '0.5rem' }}>
              <span>Sign Out</span>
              <span style={{ fontWeight: 600, color: todayRecord?.sign_out_time ? '#2563eb' : '#94a3b8' }}>
                {todayRecord?.sign_out_time ? new Date(todayRecord.sign_out_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Not recorded'}
              </span>
            </div>
          </div>

          {!done && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {canSignIn && (
                <button
                  className="btn btn-success btn-block btn-lg"
                  onClick={() => handleSubmit('in')}
                  disabled={submitting || !photo || !location}
                >
                  {submitting ? <><span className="spinner" /> Submitting...</> : '✅ Sign In'}
                </button>
              )}
              {canSignOut && (
                <button
                  className="btn btn-primary btn-block btn-lg"
                  onClick={() => handleSubmit('out')}
                  disabled={submitting || !photo || !location}
                >
                  {submitting ? <><span className="spinner" /> Submitting...</> : '🚪 Sign Out'}
                </button>
              )}
              {!photo && <p style={{ color: '#94a3b8', fontSize: '0.8rem', textAlign: 'center' }}>Take a photo to continue</p>}
              {photo && !location && <p style={{ color: '#94a3b8', fontSize: '0.8rem', textAlign: 'center' }}>Waiting for location...</p>}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
